import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Aspilon "Complete the routine" tick boxes (blocks/product-recommendations.liquid, setting "Tick boxes to add
 * with Add to cart"; Product board).
 *
 * Horizon's product form is untouched. When it adds the product on this page, it sends a CartLinesUpdateEvent
 * (context "product") with a promise; once that promise says the add worked, the ticked products in the same
 * product section are added in one request, and the same kind of event is sent with the re-rendered cart
 * sections, so the cart drawer, cart page and header count update the way they do for Horizon's own adds
 * (the pattern of assets/aspilon-cart-addon.js). The boxes are cleared afterwards. No animation (SPEC §7).
 */

const EVENT = CartLinesUpdateEvent.eventName;

document.addEventListener(EVENT, (event) => {
  if (!(event instanceof CartLinesUpdateEvent) || event.context !== 'product') return;

  const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  if (!form) return;

  const scope = form.closest('.shopify-section') ?? document;
  const rows = [...scope.querySelectorAll('.aspilon-add-with')].filter((row) => {
    const check = row.querySelector('.aspilon-add-with__check');
    return check instanceof HTMLInputElement && check.checked && !check.disabled;
  });
  if (!rows.length || !event.promise) return;

  event.promise
    .then((result) => {
      if (result?.detail?.didError) return;
      addRows(rows, form);
    })
    .catch(() => {});
});

/**
 * @param {Element[]} rows
 * @param {Element} origin
 */
async function addRows(rows, origin) {
  const items = rows.map((row) => {
    const shade = row.querySelector('.aspilon-add-with__variant input:checked');
    const check = /** @type {HTMLInputElement} */ (row.querySelector('.aspilon-add-with__check'));
    const id = shade instanceof HTMLInputElement ? shade.value : check.value;
    return { id: Number(id), quantity: 1 };
  });

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'standard-action',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const body = JSON.stringify({ items, ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}) });
    const config = fetchConfig('json', { body });
    const response = await fetch(Theme.routes.cart_add_url, config).then((result) => result.json());
    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((result) => result.json());

    if (response.status) {
      origin.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'aspilon-add-ons' },
      });
      announce(response.description || response.message);
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'aspilon-add-ons',
        itemCount: items.length,
        sections: response.sections,
      },
    });

    rows.forEach((row) => {
      const check = row.querySelector('.aspilon-add-with__check');
      if (check instanceof HTMLInputElement) check.checked = false;
    });
    announce(rows.map((row) => (row instanceof HTMLElement ? row.dataset.addedMessage : '')).join(' '));
  } catch (error) {
    deferred.reject(error);
    console.error(error);
  }
}

/**
 * Polite announcement ("Foundation added to cart"), placed in the open dialog when there is one.
 * @param {string | undefined} text
 */
function announce(text) {
  if (!text) return;
  const host = document.querySelector('dialog[open]') ?? document.body;
  let region = host.querySelector(':scope > .aspilon-add-ons-live');
  if (!region) {
    region = document.createElement('div');
    region.className = 'aspilon-add-ons-live visually-hidden';
    region.setAttribute('role', 'status');
    host.append(region);
  }
  region.textContent = '';
  requestAnimationFrame(() => {
    if (region) region.textContent = text;
  });
}
